import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SpaceTab } from 'api/space';
import {
  CpBody00Secondary,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { RocketDocument32 } from 'components/atoms/icons';
import { useIsTablet } from 'hooks/use-media-query';
import { CpSpacesEmptyState } from './cp-spaces-empty-state';

const Wrapper = styled.div``;

const File = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
  border-bottom: 1px solid var(--cp-border-subtle);
  padding-left: var(--cp-spacing-05);
  width: 100%;

  &:hover {
    background: var(--cp-gray-10);
    cursor: pointer;
  }
`;

const Th = styled(File)`
  padding-top: 0;
  padding-bottom: 0;
  height: 32px;
  background: var(--cp-coolest-gray-10);

  &:hover {
    background: var(--cp-coolest-gray-10);
    cursor: default;
  }
`;

const FileName = styled(CpBody01Primary)`
  margin-bottom: 0;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: start;
  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
  }
`;

const FileIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--cp-spacing-04);
  img,
  svg {
    width: 20px;
    height: 20px;
  }

  svg {
    fill: var(--cp-blue-50);
  }
`;

const Name = styled(CpBody00Secondary)`
  margin-bottom: 0;
  cursor: pointer;
  width: 100%;
  padding-left: var(--cp-spacing-07);
`;

const Item: React.FC<{
  spaceTab: SpaceTab;
  onClick: () => void;
}> = ({ spaceTab, onClick }) => {
  const iconSrc =
    Array.isArray(spaceTab.metaData?.links?.icon) &&
    spaceTab.metaData?.links?.icon[0]?.href;

  return (
    <File
      className="rl-cp-spaces-list-view-item"
      data-cy={`cp.spaces.list-view.item.${spaceTab.spaceTabTitle}`}
      onClick={onClick}
    >
      <FileName
        className="rl-cp-spaces-list-view-item-name"
        data-cy={`cp.spaces.list-view.item.name.${spaceTab.spaceTabTitle}`}
      >
        <FileIcon
          className="rl-cp-spaces-list-view-item-icon"
          data-cy={`cp.spaces.list-view.item.icon.${spaceTab.spaceTabTitle}`}
        >
          {iconSrc ? (
            <img src={iconSrc} alt={spaceTab.spaceTabTitle} />
          ) : (
            <RocketDocument32 className="rl-document-svg" />
          )}
        </FileIcon>
        {spaceTab.spaceTabTitle}
      </FileName>
    </File>
  );
};

export const CpSpacesListView: React.FC<{
  spaceTabs: SpaceTab[];
  onSpaceTabClick: (spaceTab: SpaceTab) => void;
}> = ({ spaceTabs, onSpaceTabClick }) => {
  const isResponsive = useIsTablet();

  return spaceTabs?.length ? (
    <Wrapper className="rl-cp-spaces-list-view" data-cy="cp.spaces.list-view">
      {!isResponsive ? (
        <>
          <Th>
            <Name>
              <FormattedMessage id="name" />
            </Name>
          </Th>
        </>
      ) : (
        <></>
      )}
      {spaceTabs.map((spt) => {
        return (
          <Item
            key={spt.spaceTabId}
            spaceTab={spt}
            onClick={() => {
              onSpaceTabClick(spt);
            }}
          />
        );
      })}
    </Wrapper>
  ) : (
    <CpSpacesEmptyState />
  );
};
